import { Box, Button, Chip, ClickAwayListener, Divider, List, ListItem, Typography, Tooltip, IconButton } from "@mui/material";
import { PickersShortcutsItem, PickersShortcutsProps, StaticDatePicker } from "@mui/x-date-pickers-pro";
import { navigate, useParams } from '@cedarjs/router';
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from 'react';
import { useDateStore } from "src/lib/store";
import AnimatedSlidingBlock from "../AnimatedSlidingBlock/AnimatedSlidingBlock";
import { AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWarning } from "@fortawesome/free-solid-svg-icons";
import { faCalendar } from "@fortawesome/free-regular-svg-icons";

type ExtendedPickersShortcutsItem = PickersShortcutsItem<Dayjs | null> & { className: string };

const shortcutsItems: ExtendedPickersShortcutsItem[] = [
  {
    label: "time.today",
    className: "bg-blue-500 hover:bg-blue-600  text-white",
    getValue: () => {
      return dayjs();
    },
  },
  {
    label: "time.lastWeek",
    className: "",
    getValue: () => {
      // (December 25)
      const today = dayjs();
      return today.subtract(1, "week");
    },
  },
  {
    label: "time.lastMonth",
    className: "",
    getValue: () => {
      // (December 25)
      const today = dayjs();
      return today.subtract(1, "month");
    },
  },
];


const WorkDatePicker = (props) => {
  const [date, setDate] = useDateStore((state) => [state.date, state.setDate]);
  const { t } = useTranslation()
  const [dateDayJs, setDateDayJs] = useState(date ? dayjs(date, 'DD-MM-YYYY').set("hour", 9) : dayjs());
  const [showCalendar, setShowCalendar] = useState(false);

  useEffect(() => {
    if (date != null) {
      setDateDayJs(dayjs(date, 'DD-MM-YYYY'))
    }
    //If an explicit null as been passed to store to restore current day, and the workdate is not set to today
    else if(date == null && !dateDayJs.isSame(dayjs(), "day"))
      setDateDayJs(dayjs())

  }, [date])

  const handleDateChange = (newDate) => {
    newDate.set("hour", 9);
    setDateDayJs(newDate)
    setDate(newDate)
    setShowCalendar(false)
  }

  const handleClickOutside = () => {
    setShowCalendar(false)
    setDate(dateDayJs)
  }

  return (
    <>
      <Button onClick={() => { setShowCalendar(!showCalendar) }} className={`sm:flex flex-col hidden items-start ${props.className}`} title={t('titles.workdate')}>
          <Typography variant="h4" className="pb-1 dark:text-white/80 text-black">
            {dateDayJs.format('DD/MM/YYYY')}
            {dateDayJs.isAfter(dayjs(), "day") && <Tooltip title={t("feedback.workingDateIsAfterToday")} arrow><FontAwesomeIcon icon={faWarning} className="ml-2 text-red-500"/></Tooltip>}
            {dateDayJs.isBefore(dayjs(), "day") && <Tooltip title={t("feedback.workingDateIsBeforeToday")} arrow><FontAwesomeIcon icon={faWarning} className="ml-2 text-orange-400"/></Tooltip>}
          </Typography>
          <Typography variant="caption">{t("calendar.week")} {dateDayJs.week()}</Typography>

      </Button>
      <IconButton onClick={() => { setShowCalendar(!showCalendar) }} className="sm:hidden block text-blue-400"><FontAwesomeIcon icon={faCalendar}/></IconButton>
      <AnimatePresence>
        {showCalendar && (
          <ClickAwayListener onClickAway={() => handleClickOutside()}>
            {/* <AnimatedSlidingBlock slideX={false} layout={false}> */}
              <StaticDatePicker
                className="absolute dark:bg-dark-950 bg-white shadow-md "
                value={dateDayJs}
                onChange={(newValue) => handleDateChange(newValue)}
                slots={{
                  shortcuts: CustomRangeShortcuts,
                }}
                slotProps={{
                  shortcuts: {
                    items: shortcutsItems
                  },
                  toolbar: {
                    hidden: true,
                  },
                  actionBar: {
                    // hidden: true,
                    actions: []
                  },
                }}
                //To prevent scrolling flash when the calendar disappears
                // reduceAnimations
              />
            {/* </AnimatedSlidingBlock> */}
          </ClickAwayListener>
        )}
      </AnimatePresence>
    </>
  )
}

export default WorkDatePicker;


function CustomRangeShortcuts(props: PickersShortcutsProps<Dayjs>) {
  const { items, onChange, isValid } = props;
  const { t } = useTranslation()

  if (items == null || items.length === 0) {
    return null;
  }

  const resolvedItems = items.map((item :ExtendedPickersShortcutsItem) => {
    const newValue = item.getValue({ isValid });

    return {
      label: t(item.label),
      onClick: () => {
        onChange(newValue);
      },
      className: item.className,
      disabled: !isValid(newValue),
    };
  });

  //https://mui.com/x/react-date-pickers/shortcuts/#customization
  return (
    <Box sx={{ gridRow: 1, gridColumn: 2, }} >

      <List
        dense
        className="px-2"
        sx={(theme) => ({
          display: 'flex',
          '& .MuiListItem-root': {
            pt: 0,
            pl: 0,
            pr: theme.spacing(1),
          },
        })}
      >
        {resolvedItems.map((item) => {
          return (
            <ListItem key={item.label}>
              <Chip {...item} className={item.className}/>
            </ListItem>
          );
        })}
      </List>
      <Divider />
    </Box>
  );
}